import { useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { Check, RotateCcw, ThumbsDown, ThumbsUp, X } from 'lucide-react'
import type { Sorteer as SorteerBlok } from '../lib/types'

/* ===========================================================================
 *  Sorteren: mag dit in de chat?
 *
 *  De belangrijkste werkvorm van de cursus, en de reden dat hij zo werkt:
 *
 *  Twee knoppen, geen slepen. Dit wordt gedaan op een tablet aan de muur bij
 *  de wasstraat, staand, met handschoenen aan. Slepen is daar een beproeving
 *  -- je mist, de kaart springt terug, je probeert het nog eens. Tikken werkt
 *  op elk toestel en met elke vinger.
 *
 *  Fout mag. Na elk antwoord staat er waarom, en je gaat gewoon door. Een
 *  werkvorm die straft leert mensen voorzichtig klikken in plaats van
 *  nadenken -- en juist het nadenken is waar het hier om gaat.
 *
 *  De volgorde is per keer anders. Niet om het moeilijker te maken, maar
 *  omdat dit de module is die mensen overdoen: bij een tweede ronde in
 *  dezelfde volgorde onthoud je de volgorde en niet de regel.
 * =========================================================================== */

export default function Sorteer({
  blok, opKlaar,
}: {
  blok: SorteerBlok
  opKlaar: () => void
}) {
  const [ronde, setRonde] = useState(0)
  const kaarten = useMemo(() => schud(blok.kaarten), [blok.kaarten, ronde])

  const [bij, setBij] = useState(0)
  const [gekozen, setGekozen] = useState<boolean | null>(null)
  const [goed, setGoed] = useState(0)
  const [weg, setWeg] = useState<'links' | 'rechts' | null>(null)

  const kaart = kaarten[bij]
  const klaar = bij >= kaarten.length

  function kies(rechts: boolean) {
    if (gekozen !== null || !kaart) return
    setGekozen(rechts)
    if (rechts === kaart.rechts) setGoed((g) => g + 1)
  }

  function volgende() {
    if (!kaart) return
    setWeg(gekozen ? 'rechts' : 'links')
    /* Even wachten tot de kaart weg is; anders wisselt de tekst terwijl hij
       nog in beeld schuift en lijkt het alsof je de verkeerde ziet wegvliegen. */
    window.setTimeout(() => {
      setWeg(null)
      setGekozen(null)
      setBij((b) => b + 1)
      if (bij + 1 >= kaarten.length) opKlaar()
    }, 210)
  }

  function opnieuw() {
    setRonde((r) => r + 1)
    setBij(0)
    setGekozen(null)
    setGoed(0)
  }

  if (klaar) {
    const deel = Math.round((goed / kaarten.length) * 100)
    return (
      <div className="sorteer-klaar opkomst">
        <div className="score" style={{ color: deel >= 80 ? 'var(--text-ok)' : 'var(--text-warn)' }}>
          {goed} van {kaarten.length}
        </div>
        <p className="toelichting">
          {deel === 100
            ? 'Alles goed. Dit zit erin.'
            : deel >= 80
              ? 'Goed. Kijk de uitleg bij de misser nog even terug.'
              : 'Dit is de module om nog een keer te doen — juist deze.'}
        </p>
        <button className="knop" onClick={opnieuw}>
          <RotateCcw size={16} /> Nog een ronde
        </button>
      </div>
    )
  }

  return (
    <div className="sorteer">
      <div className="vraag">{blok.vraag}</div>
      <div className="teller">{bij + 1} van {kaarten.length}</div>

      <div className="stapel">
        {bij + 2 < kaarten.length && <div className="stapel-onder twee" />}
        {bij + 1 < kaarten.length && <div className="stapel-onder een" />}

        {/*
          * De kaart die er LIGT komt met een CSS-animatie binnen, de kaart
          * die WEGVLIEGT met framer-motion. Dat onderscheid is met opzet:
          * mislukt de eerste animatie, dan is de vraag onleesbaar; mislukt de
          * tweede, dan verdwijnt een kaart zonder zwaai. Alleen het tweede
          * mag.
          */}
        {kaart && !weg && (
          <div key={kaart.id} className="stapel-kaart opkomst">
            {kaart.tekst}
          </div>
        )}
        {kaart && weg && (
          <motion.div
            key={`${kaart.id}-weg`}
            className="stapel-kaart"
            initial={{ opacity: 1, x: 0, rotate: 0 }}
            animate={{
              opacity: 0,
              x: weg === 'rechts' ? 320 : -320,
              rotate: weg === 'rechts' ? 12 : -12,
            }}
            transition={{ duration: .21, ease: 'easeIn' }}
          >
            {kaart.tekst}
          </motion.div>
        )}
      </div>

      <div className="keuzes">
        <button
          className="knop niet"
          onClick={() => kies(false)}
          disabled={gekozen !== null}
        >
          <ThumbsDown size={18} /> {blok.linksLabel}
        </button>
        <button
          className="knop wel"
          onClick={() => kies(true)}
          disabled={gekozen !== null}
        >
          <ThumbsUp size={18} /> {blok.rechtsLabel}
        </button>
      </div>

      {gekozen !== null && kaart && (
        <div className="opkomst" style={{ display: 'grid', gap: 12 }}>
            <div className={`uitslag ${gekozen === kaart.rechts ? 'goed' : 'mis'}`}>
              {gekozen === kaart.rechts ? <Check size={18} /> : <X size={18} />}
              <span>
                <span className="titel">
                  {gekozen === kaart.rechts ? 'Klopt.' : 'Net niet.'}
                </span>
                {kaart.uitleg}
              </span>
            </div>
          <button className="knop hoofd breed" onClick={volgende}>
            {bij + 1 >= kaarten.length ? 'Bekijk je score' : 'Volgende kaart'}
          </button>
        </div>
      )}
    </div>
  )
}

/**
 * Door elkaar, met Fisher-Yates.
 *
 * Niet met sort(() => Math.random() - .5): dat lijkt te werken en schudt
 * scheef -- sommige volgordes komen veel vaker voor dan andere. Bij veertien
 * kaarten valt dat niet op, en dat is precies waarom die truc blijft rondgaan.
 */
function schud<T>(in_: T[]): T[] {
  const uit = [...in_]
  for (let i = uit.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    const a = uit[i]!
    const b = uit[j]!
    uit[i] = b
    uit[j] = a
  }
  return uit
}
